import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { 
  Box, 
  CircularProgress,  
  IconButton,  
  Menu,  
  MenuItem,  
  Switch,  
  Table, 
  TableBody, 
  TableCell, 
  TableFooter, 
  TableHead, 
  TablePagination, 
  TableRow, 
  TableSortLabel, 
  Typography 
} from '@mui/material';

import TablePaginationActions from '../../../../../../components/utils/TablePaginationActions';
import useTablePaginationInit from '../../../../../../components/hooks/useTablePaginationInit';
import useTableSortInit from '../../../../../../components/hooks/useTableSortInit';
import TableTimeStamps from '../../../../../../components/utils/TableTimeStamps';
import Copyable from '../../../../../../components/utils/Copyable';
import TopBar from '../../../../../../components/utils/TopBar';
import NA from '../../../../../../components/utils/NA';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import { 
  GetPaymentCountriesBIZcoreCC,
  GetPaymentCountryCurrencyXrefBIZcoreCC,
  GetPaymentCurrenciesBIZcoreCC,
  UpdatePaymentCountryCurrencyXrefBIZcoreCC,
} from '../../../../../../redux/actions/business/core/currencyCloudActions';

import PaymentCountryCurrencyXrefModal from './PaymentCountryCurrencyXrefModal';

import global from "../../../../../../global.scss";

const PaymentCountryCurrencyXrefPage = () => {
  const dispatch = useDispatch();
  const state = useSelector(state => state["business.core.cc"]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [paymentCountryCurrencyXrefModalOpen, setPaymentCountryCurrencyXrefModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [update, setUpdate] = useState(null);

  const [paymentCountryCurrencyXrefList, setPaymentCountryCurrencyXrefList] = useState([]);
  const [paymentCountryCurrencyXrefTotal, setPaymentCountryCurrencyXrefTotal] = useState(0);

  const [paymentCountryCurrencyXrefMenuOpen, setPaymentCountryCurrencyXrefMenuOpen] = useState(false);
  const [paymentCountryCurrencyXrefMenuAnchor, setPaymentCountryCurrencyXrefMenuAnchor] = useState(null);

  const [
    page, 
    rowsPerPage, 
    handleChangePage, 
    handleChangeRowsPerPage
  ] = useTablePaginationInit();

  const [
    sort,
    sortDirection,
    handleSort
  ] = useTableSortInit();

  // searching from a later page would land on an empty result set
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    handleChangePage(null, 0);
  };

  const tableHeadingsList = [
    {
      label: "Id",
      sort: "id",
    },
    {
      label: "Payment Country",
      sort: "ref_payment_country_id",
    },
    {
      label: "Payment Currency",
      sort: "ref_payment_currency_id",
    },
    {
      label: "Active",
      sort: "is_active",
    },
    {
      label: "Require purpose code",
      sort: "is_require_purpose_code",
    },
    {
      label: "Time stamps",
      sort: "created_at",
    },
    {
      label: "",
      sort: null
    }
  ];

  useEffect(() => {
    dispatch(GetPaymentCountriesBIZcoreCC(1, 500, "", setLoading));
    dispatch(GetPaymentCurrenciesBIZcoreCC(1, 500, "", setLoading));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    handleGetPaymentCountryCurrencyXref();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, sort, sortDirection]);

  useEffect(() => {
    try {
      formatPaymentCountryCurrencyXrefList(state.paymentCountryCurrencyXrefList.records || []);
      setPaymentCountryCurrencyXrefTotal(state.paymentCountryCurrencyXrefList.total || 0);
    } catch (err) {}
  }, [state]);

  const formatPaymentCountryCurrencyXrefList = (list) => {
    setPaymentCountryCurrencyXrefList(list)
  };

  async function handleGetPaymentCountryCurrencyXref(customLoading){
    let query = ""
    if(search) query += `&search=${search}`;
    if(sort) query += `&ordering=${sortDirection === "asc" ? '' : '-'}${sort}`;
    await dispatch(GetPaymentCountryCurrencyXrefBIZcoreCC(page+1, rowsPerPage, query, customLoading ? customLoading : setLoading));
  };

  const handleTogglePaymentCountryCurrencyXref = (id, bool) => {
    let obj = {
      is_active: bool
    };
    dispatch(UpdatePaymentCountryCurrencyXrefBIZcoreCC(id, obj, setLoading)).then(() => {
      handleGetPaymentCountryCurrencyXref()
    })
  };

  return (
    <Box>

      <PaymentCountryCurrencyXrefModal 
        open={paymentCountryCurrencyXrefModalOpen}
        setOpen={setPaymentCountryCurrencyXrefModalOpen}
        title={modalTitle}
        update={update}
        handleGet={handleGetPaymentCountryCurrencyXref}
        />

      <TopBar
        title={"Payment Country Currency Xref"}
        search={search}
        searchPlaceholder={"Search id, country or currency"}
        onSearchChange={handleSearchChange}
        button1Text={"Add Payment Country Currency Xref"}
        button1OnClick={() => {
          setUpdate({
            defaultMode: 1
          });
          setModalTitle("Add Payment Country Currency Xref");
          setPaymentCountryCurrencyXrefModalOpen(!paymentCountryCurrencyXrefModalOpen);
        }}
        />

      {
        loading
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
          paymentCountryCurrencyXrefList.length === 0
          ?
          <Box height={"300px"} className="flexCenterCenterRow">
            <Typography>No payment country currency xref found</Typography>
          </Box>
          :
          <Table className="block">
            <TableHead>
              <TableRow>
                {
                  tableHeadingsList.map((heading, headingIndex) => (
                    heading.sort === null
                    ?
                    <TableCell
                      key={headingIndex}>{heading.label}</TableCell>
                    :
                    <TableCell
                      key={headingIndex}>
                      <TableSortLabel
                        active={sort === heading.sort ? true : false}
                        direction={sortDirection}
                        onClick={() => {
                          handleSort(heading.sort);
                        }}
                      >
                        {heading.label}
                      </TableSortLabel>
                    </TableCell>
                  ))
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {
                paymentCountryCurrencyXrefList.map((row, index) => (
                  <TableRow 
                    key={index}>
                    <TableCell>
                      <Typography
                        variant='caption'
                        color={"grey"}>
                        <Copyable>
                          {row.id || <NA />}
                        </Copyable>
                      </Typography>
                    </TableCell>
                    <TableCell>
                      ({row?.ref_payment_country_id?.ref_country_id?.code})&nbsp;{row?.ref_payment_country_id?.ref_country_id?.name}
                      <Typography
                        variant='caption'
                        color={"grey"}>
                        <Copyable>
                          {row?.ref_payment_country_id?.ref_country_id?.id || <NA />}
                        </Copyable>
                      </Typography>
                    </TableCell>
                    <TableCell>
                      ({row?.ref_payment_currency_id?.ref_currency_id?.code})&nbsp;{row?.ref_payment_currency_id?.ref_currency_id?.name}
                      <Typography
                        variant='caption'
                        color={"grey"}>
                        <Copyable>
                          {row?.ref_payment_currency_id?.ref_currency_id?.id || <NA />}
                        </Copyable>
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={row.is_active}
                        onChange={(e) => {
                          handleTogglePaymentCountryCurrencyXref(row.id, e.target.checked);
                        }}
                        />
                    </TableCell>
                    <TableCell>{row.is_require_purpose_code ? "Yes" : "No"}</TableCell>
                    <TableCell>
                      <TableTimeStamps
                        createdAt={row.created_at}
                        updatedAt={row.updated_at}
                        />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={(e) => {
                          setPaymentCountryCurrencyXrefMenuAnchor(e.currentTarget);
                          setPaymentCountryCurrencyXrefMenuOpen(true);
                          setUpdate(row);
                          setModalTitle(row.name || "");
                        }}>
                        <MoreVertIcon sx={{ color: global['secondary_light']}}/>
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  sx={{ borderBottom: 0 }}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]} 
                  colSpan={10}
                  count={paymentCountryCurrencyXrefTotal}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: {
                      'aria-label': 'rows per page'
                    },
                    native: true
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                  />
              </TableRow>
            </TableFooter>
          </Table>
      }
      <Menu
        open={paymentCountryCurrencyXrefMenuOpen}
        anchorEl={paymentCountryCurrencyXrefMenuAnchor}
        onClose={() => setPaymentCountryCurrencyXrefMenuOpen(false)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem
          onClick={() => {
            setModalTitle("Edit " + modalTitle);
            setUpdate({
              ...update,
              defaultMode: 2
            });
            setPaymentCountryCurrencyXrefMenuOpen(false);
            setPaymentCountryCurrencyXrefModalOpen(!paymentCountryCurrencyXrefModalOpen);
          }}>
          <Box
            className="flexCenter_Row"
            gap={"10px"}>
            <EditOutlinedIcon />
            Edit
          </Box>
        </MenuItem>
      </Menu>
    </Box>
  )
}

export default PaymentCountryCurrencyXrefPage;